import { useEffect, useId, useMemo, useState } from "react";
import { LuHandshake } from "react-icons/lu";
import { Input, Modal } from "@iq/models";
import { createDeal } from "../../services/contact_service";
import useLookupStore from "../../states/useLookupStore";
import "./Modal.css";
import { LOST_REASONS } from "../../models/dealClosureModel";
import { ACQUISITION_SOURCES, PRIMARY_OFFERS } from "../../models/opportunityModel";

const DEAL_STAGES = ["discovery", "demo", "negotiation", "won", "lost"];
const CURRENCIES = ["USD", "EUR", "GBP", "CAD"];

function CreateDeal({
    isOpen,
    onClose,
    contactId,
    initialAccount = "",
    initialAccountId = null,
    products = [],
    productsLoading = false,
    productsError = "",
    onCreated,
}) {
    const formId = useId();
    const [form, setForm] = useState({
        dealName: "",
        account: initialAccount || "",
        accountId: initialAccountId,
        productId: "",
        currency: "USD",
        dealValue: "",
        dealStage: "discovery",
        lostReason: "",
        acquisitionSource: "",
        primaryOffer: "",
    });
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");
    const accounts = useLookupStore((state) => state.accounts);
    const accountsError = useLookupStore((state) => state.accountsError);
    const hydrateAccounts = useLookupStore((state) => state.hydrateAccounts);

    useEffect(() => {
        if (isOpen) {
            hydrateAccounts();
        }
    }, [hydrateAccounts, isOpen]);

    useEffect(() => {
        if (!isOpen) {
            return;
        }

        setForm({
            dealName: "",
            account: initialAccount || "",
            accountId: initialAccountId,
            productId: "",
            currency: "USD",
            dealValue: "",
            dealStage: "discovery",
            lostReason: "",
            acquisitionSource: "",
            primaryOffer: "",
        });
        setSaving(false);
        setError("");
    }, [isOpen, initialAccount, initialAccountId]);

    const productOptions = useMemo(() => {
        if (!Array.isArray(products)) {
            return [];
        }

        return products
            .map((product) => {
                const id = String(product?.id ?? product?.product_id ?? "");
                const label = product?.name || product?.product_name || "Unnamed product";

                if (!id) {
                    return null;
                }

                return { id, label };
            })
            .filter(Boolean);
    }, [products]);

    const selectedProductLabel = useMemo(() => {
        if (!form.productId) {
            return "";
        }

        const selected = productOptions.find((product) => product.id === String(form.productId));
        return selected?.label || "";
    }, [form.productId, productOptions]);

    const canSave = useMemo(() => {
        return Boolean(form.dealName.trim())
            && Boolean(form.accountId)
            && Boolean(form.productId)
            && Boolean(form.acquisitionSource)
            && Boolean(form.primaryOffer)
            && Boolean(form.dealValue)
            && (form.dealStage !== "lost" || Boolean(form.lostReason))
            && !productsLoading
            && !saving;
    }, [form.accountId, form.acquisitionSource, form.dealName, form.dealStage, form.dealValue, form.lostReason, form.primaryOffer, form.productId, productsLoading, saving]);

    const actionButtons = useMemo(() => ([
        {
            key: "create-deal",
            text: saving ? "Creating..." : "Create Deal",
            icon: <LuHandshake />,
            type: "submit",
            form: formId,
            disabled: !canSave,
            color: "accent",
        },
    ]), [canSave, formId, saving]);

    function updateField(field, value) {
        setForm((prev) => ({ ...prev, [field]: value }));
    }

    async function handleSubmit(event) {
        event.preventDefault();
        if (!canSave) {
            return;
        }

        setError("");
        setSaving(true);

        try {
            const created = await createDeal({
                contactId,
                accountId: form.accountId,
                account: form.account.trim(),
                dealName: form.dealName.trim(),
                acquisitionSource: form.acquisitionSource,
                primaryOffer: form.primaryOffer,
                currency: form.currency,
                dealValue: form.dealValue,
                dealStage: form.dealStage,
                lostReason: form.lostReason,
                productId: Number(form.productId),
            });

            onCreated?.(created);
            onClose?.();
        } catch (submitError) {
            setError(submitError?.message || "Failed to create deal");
        } finally {
            setSaving(false);
        }
    }

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title="Create Deal"
            icon={<LuHandshake />}
            actionButtons={actionButtons}
            errorMessage={error || accountsError}
            shrink={true}
        >
            <form id={formId} className="modal-form-container" onSubmit={handleSubmit}>
                <div className="form-fields-container">
                    <div className="modal-form-field wide-form-field">
                        <label htmlFor="deal-name">Deal Name <span style={{ color: "var(--color-danger)" }}>*</span></label>
                        <Input
                            id="deal-name"
                            name="dealName"
                            type="text"
                            placeholder="E.g. Q3 Expansion - Acme Inc."
                            value={form.dealName}
                            onChange={(event) => updateField("dealName", event.target.value)}
                            colorScheme="light"
                        />
                    </div>

                    <div className="modal-form-field">
                        <label htmlFor="deal-account">Account <span style={{ color: "var(--color-danger)" }}>*</span></label>
                        <Input
                            type="dropdown"
                            id="deal-account"
                            name="account"
                            placeholder="Select account"
                            value={form.account}
                            allowCustom={false}
                            colorScheme="light"
                            options={accounts.map((account) => ({ value: account.id, label: account.name }))}
                            onOptionSelect={(option) => setForm((current) => ({ ...current, accountId: option.value, account: option.label }))}
                        />
                    </div>

                    <div className="modal-form-field">
                        <label htmlFor="deal-product">Product <span style={{ color: "var(--color-danger)" }}>*</span></label>
                        <Input
                            type="dropdown"
                            id="deal-product"
                            name="productId"
                            placeholder={productsLoading ? "Loading products..." : "Select product"}
                            value={selectedProductLabel}
                            allowCustom={false}
                            colorScheme="light"
                            ariaLabel="Product options"
                            disabled={productsLoading}
                            options={productOptions.map((product) => ({ value: product.id, label: product.label }))}
                            onOptionSelect={(option) => updateField("productId", option.value)}
                        />
                        {!productsLoading && productsError && (
                            <span className="modal-form-field-note" style={{ color: "var(--color-danger)" }}>
                                {productsError}
                            </span>
                        )}
                    </div>

                    <div className="modal-form-field">
                        <label htmlFor="deal-acquisition-source">Acquisition Source <span style={{ color: "var(--color-danger)" }}>*</span></label>
                        <Input
                            type="dropdown"
                            id="deal-acquisition-source"
                            placeholder="Select source"
                            value={ACQUISITION_SOURCES.find((option) => option.value === form.acquisitionSource)?.label || ""}
                            allowCustom={false}
                            colorScheme="light"
                            options={ACQUISITION_SOURCES}
                            onOptionSelect={(option) => updateField("acquisitionSource", option.value)}
                        />
                    </div>

                    <div className="modal-form-field">
                        <label htmlFor="deal-primary-offer">Primary Offer <span style={{ color: "var(--color-danger)" }}>*</span></label>
                        <Input
                            type="dropdown"
                            id="deal-primary-offer"
                            placeholder="Select offer"
                            value={PRIMARY_OFFERS.find((option) => option.value === form.primaryOffer)?.label || ""}
                            allowCustom={false}
                            colorScheme="light"
                            options={PRIMARY_OFFERS}
                            onOptionSelect={(option) => updateField("primaryOffer", option.value)}
                        />
                    </div>

                    <div className="modal-form-field">
                        <label htmlFor="deal-stage">Stage <span style={{ color: "var(--color-danger)" }}>*</span></label>
                        <Input
                            type="dropdown"
                            id="deal-stage"
                            name="dealStage"
                            placeholder="Select stage"
                            value={form.dealStage ? form.dealStage.charAt(0).toUpperCase() + form.dealStage.slice(1) : ""}
                            allowCustom={false}
                            colorScheme="light"
                        >
                            {({ closeMenu }) => (
                                <>
                                    {DEAL_STAGES.map((stage) => (
                                        <button
                                            key={stage}
                                            type="button"
                                            className="dropdown-menu-item"
                                            onClick={() => {
                                                setForm((current) => ({
                                                    ...current,
                                                    dealStage: stage,
                                                    lostReason: stage === "lost" ? current.lostReason : "",
                                                }));
                                                closeMenu();
                                            }}
                                        >
                                            {stage.charAt(0).toUpperCase() + stage.slice(1)}
                                        </button>
                                    ))}
                                </>
                            )}
                        </Input>
                    </div>

                    {form.dealStage === "lost" ? (
                        <div className="modal-form-field">
                            <label htmlFor="create-deal-lost-reason">Lost Reason <span style={{ color: "var(--color-danger)" }}>*</span></label>
                            <Input
                                type="dropdown"
                                id="create-deal-lost-reason"
                                value={LOST_REASONS.find((reason) => reason.value === form.lostReason)?.label || ""}
                                placeholder="Select a reason"
                                allowCustom={false}
                                colorScheme="light"
                                options={LOST_REASONS}
                                onOptionSelect={(option) => updateField("lostReason", option.value)}
                            />
                        </div>
                    ) : null}

                    <div className="modal-form-field">
                        <label htmlFor="deal-value">Deal Value <span style={{ color: "var(--color-danger)" }}>*</span></label>
                        <Input
                            id="deal-value"
                            name="dealValue"
                            type="number"
                            min="0"
                            step="0.01"
                            placeholder="E.g. 5000"
                            value={form.dealValue}
                            onChange={(event) => updateField("dealValue", event.target.value)}
                            colorScheme="light"
                        />
                    </div>

                    <div className="modal-form-field">
                        <label htmlFor="deal-currency">Currency <span style={{ color: "var(--color-danger)" }}>*</span></label>
                        <Input
                            type="dropdown"
                            id="deal-currency"
                            name="currency"
                            placeholder="Select currency"
                            value={form.currency}
                            allowCustom={false}
                            colorScheme="light"
                            ariaLabel="Currency options"
                            renderInPortal={true}
                            options={CURRENCIES.map((currency) => ({ value: currency, label: currency }))}
                            onOptionSelect={(option) => updateField("currency", option.value)}
                        />
                    </div>
                </div>
            </form>
        </Modal>
    );
}

export default CreateDeal;
